import r from '../Utils/fetch'

const w: any = window
const endPoint = `${w.API_BILLING_URL}`

/** Une ligne du registre produit (pricing v2). Les 6 produits sont toujours renvoyés. */
export interface IProductLine {
	product: string
	quantity: number
	options: string[]
	source: 'stripe' | 'admin'
	/** Ligne contrat : coché, la quantité convenue cesse de plafonner. Sans effet sur une ligne Stripe. */
	unlimited: boolean
	/** true = la quantité plafonne (le défaut partout) ; false = contrat marqué illimité. */
	blocking: boolean
	/** Usage réel mesuré (admin). */
	usage?: number
}

/**
 * Le compte a-t-il accès à ce produit ?
 *
 * Quantité non nulle, ou ligne de contrat « Illimité » : sur celle-ci la
 * quantité est indicative, et 0 s'y lit « aucune quantité convenue », pas
 * « aucun accès ». Même règle que `models.ProductEntitlement.HasAccess` (SDK
 * Go) et que `hasProductAccess` dans ui-public — les trois doivent rester
 * d'accord, c'est leur divergence qui a produit le bug.
 */
export const hasProductAccess = (line: { quantity: number; blocking: boolean } | undefined | null): boolean =>
	!!line && (line.quantity > 0 || !line.blocking)

export const PRODUCT_LABELS: Record<string, { label: string; unit: string }> = {
	connect: { label: 'Connect', unit: 'comptes source' },
	activate: { label: 'Activate', unit: 'synchros inversées' },
	// Une place Track = un serveur sGTM et/ou un webhook analytics (deux quotas
	// comptés contre la même quantité). `track_webhook` a été retiré le 2026-10-20 :
	// les webhooks classiques se facturent en Connect.
	track_server: { label: 'Track', unit: 'places (serveur sGTM et/ou webhook analytics)' },
	store: { label: 'Store', unit: 'projets managés' },
	ask_ai: { label: 'Ask AI', unit: 'utilisateurs' },
	mmm: { label: 'MMM', unit: 'modèles' },
}

export const listProducts = (billingAccountId: string): Promise<IProductLine[]> =>
	r({ url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/products`, method: 'GET' })

export const setAdminProducts = (
	billingAccountId: string,
	lines: Array<{ product: string; quantity: number; options?: string[]; unlimited?: boolean }>
): Promise<IProductLine[]> =>
	r({
		url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/products`,
		method: 'PUT',
		data: { lines },
	})

export const removeAdminProduct = (billingAccountId: string, product: string): Promise<IProductLine[]> =>
	r({
		url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/products/${product}`,
		method: 'DELETE',
	})

/** Passe le compte sous contrat : toutes les lignes en admin, sans rien couper. */
export const setContract = (billingAccountId: string): Promise<IProductLine[]> =>
	r({
		url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/products/contract`,
		method: 'POST',
	})

// ---- Détenteurs d'un produit ----

/** Un compte vu sous l'angle d'un seul produit : son droit et sa consommation. */
export interface IProductHolder {
	billingAccountId: string
	name: string
	organisationName: string
	/**
	 * 'contract' = sous contrat (Pennylane) ; 'stripe' = libre-service.
	 * ⚠️ Même règle que la liste des comptes : au moins une ligne produit posée en
	 * admin. Ce n'est PAS la colonne `billingAccountType`, vide sur un compte passé
	 * sous contrat après sa création.
	 */
	billingMode: 'contract' | 'stripe'
	quantity: number
	options: string[]
	source: 'stripe' | 'admin'
	unlimited: boolean
	blocking: boolean
	usage: number
	updatedAt?: string
}

/**
 * Les comptes concernés par un produit. Rend aussi ceux qui le CONSOMMENT sans
 * y avoir droit (quantité 0, usage > 0) : c'est de la consommation non facturée,
 * et rien d'autre dans l'admin ne la signale.
 */
export const getProductHolders = (product: string): Promise<IProductHolder[]> =>
	r({ url: `${endPoint}/v1/admin/products/${product}/holders`, method: 'GET' })

// ---- Catalogue produit (lu dans Stripe) ----

export interface IProductTier {
	upTo: number
	flatAmount: number
	unitAmount: number
}

export interface IProductPrice {
	stripePriceId: string
	interval: string
	currency: string
	billingScheme: string
	tiersMode?: string
	unitAmount: number
	tiers?: IProductTier[]
}

export interface IProductCatalogItem {
	product: string
	name: string
	description?: string
	unit: string
	includedQty: number
	stripeProductId: string
	price: IProductPrice | null
	terms?: Record<string, string>
}

export const getCatalog = (): Promise<IProductCatalogItem[]> =>
	r({ url: `${endPoint}/v1/public/products`, method: 'GET' })

/** Rejoue la photo des droits (force = recalcule aussi les comptes déjà photographiés ; les lignes contrat sont conservées). */
export const backfillProducts = (force: boolean): Promise<{ processed: number; written: number; skipped: number; errors: string[] }> =>
	r({ url: `${endPoint}/v1/admin/product-entitlements/backfill${force ? '?force=true' : ''}`, method: 'POST' })

/** Purge le catalogue produit caché et relit Stripe (après une modification de prix). */
export const refreshCatalog = (): Promise<IProductCatalogItem[]> =>
	r({ url: `${endPoint}/v1/admin/products/refresh-catalog`, method: 'POST' })

// ---- Dépassements (Store / Track), tels qu'ils seraient facturés ----

export interface IUsageLine {
	product: string
	ref: string
	label: string
	quantity: number
	amountCents: number
	periodStart: string
	periodEnd: string
}

export interface IUsageLinesReport {
	from: string
	to: string
	lines: IUsageLine[]
	totalCents: number
	termsFromCatalog: boolean
}

export const getUsageLines = (billingAccountId: string, from?: string, to?: string): Promise<IUsageLinesReport> => {
	const qs = new URLSearchParams()
	if (from) qs.set('from', from)
	if (to) qs.set('to', to)
	const suffix = qs.toString() ? `?${qs.toString()}` : ''
	return r({ url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/usage-lines${suffix}`, method: 'GET' })
}
